import React, { FC, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import { ExternalLink, FlexBox, Skeleton, useBreakpoint, useIsMobile } from '@brg/ui';
import { TFile, Project, Competition, Card } from "@brg/api";
import { COVER_VIEWTYPES_1_9, ProjectCardContainerNew, ProjectCardWrapperDoubleNew, ProjectCardWrapperNew } from './stylesNew';
import { CreateLinkTo } from '../../constants/linkTo';
import './ProjectCardLayoutNew.css';
import { AppTester } from "../../../supporting";
import useIsDoubleGrid from "../../hooks/useIsDoubleGrid";
import ResizedImageEnhanced from "../ResizedImageEnhanced";
import { TeamHolder } from '../TeamCard/TeamHolder';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import { LayoutConst } from 'core/modules/layout.module';
import { ProjectDesignerWrapper } from "../projectDesignerName/styles";
import { SP_VOTE_COLLECTION_SIGN } from "../../pages/VotesAdmin/constants";
import { VoteCollectionCover } from "../../pages/projectEdit/components/VoteCollectionCover/VoteCollectionCover";
import { ModerationType } from "../../pages/teams/const/ModerationTypes";
import { LMSModuleCover } from "../LMSModuleCover/LMSModuleCover";
import { WarholCardCover } from "../WarholCardCover/WarholCardCover";
import { ReactComponent as AgeRestrictedIcon } from '../../assets/ageRestricted.svg';
import { RatingImgGenerate } from 'shared/pages/opencallsPage/GetCompetitionItem/components/RatingImgGenerate';
import { ZharKnigaBadge } from 'shared/pages/opencallsPage/GetCompetitionItem/components/ZharKnigaBadge';
import { useProfile } from '@brg/auth';
import { DesignerCertCardImage } from 'shared/pages/designer/components/tabs/designerDiscountstab';
import { useTranslation } from "react-i18next";
import { ActionsTools } from './components/ActionsTools';
import { OverflowContainer } from './styles';
import { ChannelDefaultEmblem } from 'shared/pages/exploreHseContextsNew/components/ChannelDefaultEmblem';
import { link } from 'fs';
import { BookIconRedMore, BookIconRedMoreDot } from '_deziiign/books/core/components/bookCard/style';
import { useZharKnigaWinner } from 'shared/hooks/useZharKnigaWinner';
import RankBadge from './components/RankBadge';
import mockAnnotations from './assets/mockAnnotanions.json';
import TruncateTextOverflow from '../TruncateTextOverflow/TruncateTextOverflow';
import stripHtml from 'shared/helpers/stripHtml';


const TitleMobile = styled.div<{
  isExpanded?: boolean;
  isClampable?: boolean;
}>`
  margin-top: 16px;

  @media (max-width: ${LayoutConst.screen.md + 'px'}) {
    margin-top: 8px;
  }

  div {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ isExpanded, isClampable }) =>
    isClampable ? (isExpanded ? 'unset' : 3) : 'unset'};
    overflow: ${({ isExpanded, isClampable }) =>
    isClampable ? (isExpanded ? 'visible' : 'hidden') : 'visible'};
    text-overflow: ellipsis;
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 15px;
    line-height: 20px;
    color: ${({ theme }) => theme.projectView.titleColor};
    transition: all 0.3s ease-in-out;
  }
`;


const SubTitleMobile = styled.div`

  div {
    color: ${({ theme }) => theme.projectView.titleColor};
  }
`;

const AuthorMobile = styled.div<{ isDoubleGrid?: boolean, hasSubtitle?: boolean }>`
  margin-top: 8px;
  ${ProjectDesignerWrapper} {
    color: #909090;
  }
  :hover {
    ${ProjectDesignerWrapper} {
      color: ${(p) => p.theme.multipleAuthors.colorHover} !important;
      svg path {
          fill: ${(p) => p.theme.projectView.authorColorHover};
      }
    }
  }

  @media (max-width: ${LayoutConst.screen.md + 'px'}) {
    margin-top: ${({ hasSubtitle }) => hasSubtitle ? 8 : 4}px;
    span {
      color: ${({ theme }) => theme.projectView.madeBy};
    }
  }
  
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: ${({ theme }) => theme.projectView.madeBy};
  
 
`;

export const CoverImage = styled(ResizedImageEnhanced) <{ absolute?: boolean, isSquare?: boolean, isCGRAVE?: boolean, isTeam?: boolean, isLargeProject?: boolean }>`
  position: ${({ absolute }) => absolute ? 'absolute' : 'static'};
  flex-shrink: 0;
  top: 0;
  left: 0;
  height: ${({ isSquare }) => isSquare ? 'initial' : '100%'};
  background: ${({ isCGRAVE }) => isCGRAVE ? '#464645' : ''};
  ${({ isTeam, isLargeProject }) => (isTeam || isLargeProject) && css`
    @media (max-width: ${LayoutConst.screen.s + 'px'}) {
        img{
          object-fit: cover;
        }
      }
  `}
  
`

const MobileMoreWrapper = styled(FlexBox) <{ backgroundStyles?: any }>`
  position: absolute;
  top: 0;
  width: 100%;
  // height: 100%;
  padding-right: 8px;

  ${({ backgroundStyles }) => backgroundStyles && css`
    ${backgroundStyles}
    height: 100%;
  `};
`

export interface ProjectCardLayoutNewProps {
  isLoading?: boolean;
  grants?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  isOneColumn?: boolean;
  coverTitle?: ReactNode;
  author?: ReactNode;
  likes?: ReactNode;
  point?: ReactNode;
  status?: ReactNode;
  backgroundText?: ReactNode;
  img?: string;
  titleImages?: TFile[];
  identifier?: string;
  project?: Project & any;
  team?: ComputedTeam;
  onClick?: (projectId?: string, e?: any) => void;
  isMoreVisible?: boolean;
  isRejected?: boolean;
  more?: ReactNode;
  href?: string;
  width?: string;
  height?: string;
  isModal?: boolean;
  statusText?: ReactNode;
  rank?: number;
  metaAlwaysVisible?: boolean;
  tooltipOpen?: boolean;
  needLmsCover?: boolean;
  toggleTooltip?: Function;
  linkPrefix?: string;
  isSelectRightNeed?: boolean;
  toggleViewPoint?: (flag: boolean) => void;
  sizeMult?: number;
  withoutGoToProject?: boolean;
  isMobileMoreVisible?: boolean;
  isRemovableFromBook?: boolean;
  deleteButton?: ReactNode;
  bookmarkButton?: ReactNode;
  repostButton?: ReactNode;
  bookmarkHovered?: boolean;
  isTeam?: boolean;
  isBookChapter?: boolean;
  isBook?: boolean;
  hasBottomText?: boolean;
  projectType?: string;
  lmsCardType?: string;
  itemType?: string;
  linkTo?: any;
  linkParams?: string;
  isCert?: boolean;
  card?: Card;
  backgroundStyles?: any;
  topBadge?: ReactNode;
  showAnnotaions?: boolean;
}


const AgeRestrictedOverlayRoot = styled(FlexBox)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(222, 222, 222, 0.60) 0%, rgba(92, 92, 92, 0.60) 100%);
  backdrop-filter: blur(10px);
  z-index: 1;
  align-items: center;
  justify-content: center;
`

export const AgeRestrictedOverlay = () => {
  return <AgeRestrictedOverlayRoot>
    <AgeRestrictedIcon />
  </AgeRestrictedOverlayRoot>
}

const Link = styled(ExternalLink) <{ span: number }>`
  display: initial;
  grid-column: span ${({ span }) => span};
  width: 100%;
  max-height: 536px;

  @media (max-width: ${LayoutConst.screen.l + 'px'}) {
    max-height: 410px;
  }
`
const TopBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
`

const ProjectCardLayoutNew: FC<ProjectCardLayoutNewProps> = ({
  isLoading,
  isMoreVisible,
  isRejected,
  isOneColumn,
  grants,
  title,
  subtitle,
  author,
  likes,
  more,
  status,
  width,
  height,
  team,
  statusText,
  metaAlwaysVisible,
  isMobileMoreVisible,
  onClick,
  isBook,
  isModal,
  identifier,
  point,
  rank,
  titleImages,
  isSelectRightNeed,
  sizeMult = 1,
  tooltipOpen = false,
  toggleViewPoint,
  toggleTooltip,
  linkPrefix,
  linkTo,
  withoutGoToProject,
  isRemovableFromBook,
  deleteButton,
  bookmarkButton,
  repostButton,
  isTeam,
  isBookChapter,
  backgroundText,
  backgroundStyles,
  projectType: viewType,
  lmsCardType,
  project,
  itemType,
  linkParams = '',
  coverTitle,
  needLmsCover,
  hasBottomText,
  isCert,
  card,
  topBadge,
  showAnnotaions
}) => {
  const location = useLocation();
  // const isMobile = useIsMobile();
  const isMobile = useBreakpoint(LayoutConst.screen.s - 1)
  const isDesktop = useBreakpoint(LayoutConst.screen.l + 1);
  const isAgeytomesh = AppTester.isAgeytomesh();
  const isDvaDesign = AppTester.isDvaDesign();
  const isSuperPuper = AppTester.isSuperPuper();
  const isCompetition = AppTester.isCompetition();
  const isMedia = AppTester.isMediiia();
  const isSuperPuperMain = useMemo(() => isSuperPuper && location.pathname === '/', [isSuperPuper, location.pathname])
  const isSuperPuperProject = useMemo(() => project?.contexts?.includes('super-puper-collection'), [project])
  const profile = useProfile();
  const isDoubleGrid = useIsDoubleGrid();
  const isSquare = false;
  const is743px = useBreakpoint(743);
  const theme = useTheme();
  const { i18n: { language } } = useTranslation();
  const isHSEDesign = AppTester.isHSEDesign();
  const containerRef = useRef<HTMLAnchorElement>(null);

  const teamWithoutImage = isTeam && team && (!titleImages || titleImages?.length === 0);

  const hasRootTeam = !!project?.rootTeamId;
  const rootTeamRoute = project?.rootTeam?.route;

  const [isExpanded, setIsExpanded] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isProgramsTab = searchParams.get('tab') === 'programs'
  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Добавьте эту строку
    setIsExpanded(!isExpanded);
  };

  const is1_9 = COVER_VIEWTYPES_1_9.includes(String(viewType)) || showAnnotaions;
  const isCollectionVote = useMemo(() => {
    return (!titleImages || !Boolean(titleImages?.length)) && (project?.projectContexts?.includes(SP_VOTE_COLLECTION_SIGN) || project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN))
  }, [project, titleImages])

  const annotation = useMemo(() => {
    if (project) {
      const { annotationText, annotationText_en } = project?.additionalInfo || {};
      if (language === 'en' && annotationText_en) {
        return annotationText_en;
      }
      return annotationText;
    }
  }, [project, language])

  const shouldDisplayAnnotation = useMemo(() => {
    return showAnnotaions && annotation?.length > 0;
  }, [showAnnotaions, annotation])

  const isWrongContext = useMemo(() => {
    return !!project && (
      (isDvaDesign && !(project?.contexts?.includes('dvadesign') || project?.projectContexts?.includes('dvadesign'))));
  }, [project])

  const onLinkClick = useCallback((e: any) => {
    if (hasRootTeam) {
      window.open(rootTeamRoute, '_self');
    } else if (isWrongContext) {
      window.open(process.env.REACT_APP_URL_DEZIIIGN + '/project/' + identifier);
    } else {
      onClick?.(identifier ?? '', e);
    }
  }, [onClick, identifier, isWrongContext])

  const isLMS = useMemo(() => team?.moderationType === ModerationType.LMS, [team?.moderationType])

  const isChannel = useMemo(() => team?.moderationType === ModerationType.Channel, [team?.moderationType])

  // const isUnitCourse = useMemo(() => {
  //   //@ts-ignore
  //   return team?.contexts?.includes(contextName)
  // } , [contextName, team])

  const ratingCompetition = project?.competitions?.find((item: Competition) => item.contexts?.includes("ratings"))
  // @ts-ignore
  const isRatingWinner = ratingCompetition?.nominations?.[0]?.winnerPositions?.some(item => item?.winnerId === project?.projectId) ?? false;

  // Жар-книга competition - проверяем через хук, является ли проект победителем
  const { isWinner: isZharKnigaWinner } = useZharKnigaWinner(project?.projectId);

  const isLMSProject = useMemo(() => project?.contexts?.includes('lmshseproject') || project?.contexts?.includes('lmsproject'), [project])

  const needLMSTeamCover = useMemo(
    () => ((needLmsCover || isLMS) && teamWithoutImage)
    , [isLMS, teamWithoutImage, needLmsCover])

  const LMSDiscipline = useMemo(() => project?.additionalInfo?.lmsmodule_discipline || '', [project])
  const [onhoverPlate, setOnhoverPlate] = useState(true);

  const onContainerMouseLeave = (ev: React.SyntheticEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    toggleTooltip && toggleTooltip(false);
    toggleViewPoint && toggleViewPoint(false);
    setOnhoverPlate(true);
  }

  const onContainerMouseEnter = () => {
    toggleViewPoint && toggleViewPoint(true);
    setOnhoverPlate(false);
  }

  const calcGridColumn = useMemo(
    () => (['t-shape', 'fixed-ratio-small', 'fixed-ratio-large', 'horizontal', 'animated'].includes(String(isTeam ? lmsCardType : viewType)) && !isOneColumn ? 2 : 1), [isOneColumn, lmsCardType, isTeam])

  const isLargeProject = calcGridColumn === 2;


  const isBookMarkHidden = useMemo(() => isSuperPuperProject || isRemovableFromBook, [isSuperPuperProject, isRemovableFromBook])

  const isRepostHidden = useMemo(() => {
    return !profile || isTeam || isLMS || isLMSProject || !!team || !!project?.teams || !!project?.rootTeam
  }, [isTeam, isLMS, isLMSProject, profile, team, project])

  const cardBgColor = useMemo(() => {
    return undefined;
  }, [])

  const actionsVisible = useMemo(() => {
    return {
      loading: isLoading,
      showRemove: !(isLoading || !isRemovableFromBook),
      showMore: !(isLoading || !isMoreVisible || isSelectRightNeed),
      showRepost: !(isLoading || isRepostHidden),
      showBookmark: !(isLoading || isBookMarkHidden),
      showStatus: !(isLoading || !isRejected || !status),
      showStatusText: !(isLoading || !statusText),
      showSelect: false,
      showBannedStatus: project?.status === 3,
    }
  }, [
    isRejected,
    isLoading,
    isMoreVisible,
    statusText,
    isRemovableFromBook,
    isBookMarkHidden,
    isRepostHidden,
    isSelectRightNeed,
    status
  ])

  const onDragStart = (e: React.DragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragOver = (e: React.DragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
  };

  const calcTitle = useMemo(() => {
    if (!!project?.additionalContent) {
      return project?.additionalContent?.title?.text
    }
    return project?.title
  }, [project])

  const linkTitle = useMemo(() => {
    if (backgroundText) {
      return undefined
    }
    return !!calcTitle ? `${language === 'ru' ? 'Ссылка на проект' : 'Link to project'} - ${calcTitle}` : undefined
  }, [project, calcTitle, backgroundText, language])

  const linkHref = useMemo(() => {
    if (isModal || isWrongContext || backgroundText) return undefined
    return !!linkTo ? linkTo : isLoading ? undefined : hasRootTeam ? rootTeamRoute : ((linkPrefix ? linkPrefix : "") + (CreateLinkTo.projectNew(project) + linkParams))
  }, [isModal, withoutGoToProject, backgroundText, isWrongContext, linkTo, isLoading, hasRootTeam, rootTeamRoute, linkPrefix, linkParams])

  return (
    <Link
      {...linkHref ? { to: linkHref } : {}}
      ref={containerRef}
      onClick={onLinkClick}
      span={calcGridColumn}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      title={linkTitle}
    >
      {!location.search.includes('display=doublegrid') && (
        <ProjectCardContainerNew
          onMouseLeave={onContainerMouseLeave}
          onMouseEnter={onContainerMouseEnter}
          {...(isMobile ? {
            onTouchStart: onContainerMouseLeave
          } : {})}
          tooltipOpen={tooltipOpen} // У активной карточки увеличивается z-index и tooltip начинает норм работать 
          width={width}
          height={height}
          isLoading={isLoading}
          isSquare={isSquare}
          noGrid={!isDoubleGrid}
          sizeMult={sizeMult}
          isAgeytomesh={isAgeytomesh}
          cardBgColor={cardBgColor}
          viewType={viewType}
          itemType={itemType}
          hasBottomText={hasBottomText}
        >
          {isBook && !isMobile && <BookIconRedMore><BookIconRedMoreDot isBlue={isHSEDesign} /><BookIconRedMoreDot isBlue={isHSEDesign} /><BookIconRedMoreDot isBlue={isHSEDesign} /></BookIconRedMore>}

          <OverflowContainer isAdaptiveCard={project?.isAdaptiveCard}>
            {project?.isAgeRestricted && <AgeRestrictedOverlay />}
            {/*@ts-ignore*/}
            {!isLoading && (isCert && !!card ? (
              <DesignerCertCardImage card={card} />
            ) : project?.isAdaptiveCard ? (
              <WarholCardCover project={project} />
            ) : isCollectionVote ? (
              <VoteCollectionCover project={project} />
            ) : (teamWithoutImage && !isLMS) ? (
              <ChannelDefaultEmblem team={team} />
            ) : (isLMSProject || needLMSTeamCover) ? (
              <LMSModuleCover project={project} title={coverTitle || title} discipline={LMSDiscipline} />
            ) : (
              <CoverImage
                isSquare={isSquare}
                showEmptyPlaceholder={!isBookChapter}
                showDefaultPlaceholder={isBookChapter}
                files={titleImages}
                isTeam={isTeam}
                isLargeProject={isLargeProject}
                alt={project?.title}
              />
            ))}
            {point}
          </OverflowContainer>
          {/* Шильдики конкурсов */}
          {(isRatingWinner || isZharKnigaWinner) && (
            <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {isRatingWinner && ratingCompetition?.isRating && (
                <RatingImgGenerate competition={ratingCompetition} isGridPlate={true} onhoverPlate={!onhoverPlate} />
              )}
              {isZharKnigaWinner && (
                <ZharKnigaBadge isGridBadge={true} onhoverPlate={!onhoverPlate} />
              )}
            </div>
          )}
          {/* Плашка партнёрского материала */}
          {topBadge && (
            <TopBadge>
              {topBadge}
            </TopBadge>
          )}
          {isMobile && isLoading &&
            <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'100%'} />
          }
          <ProjectCardWrapperNew
            className={metaAlwaysVisible ? 'meta--visible' : ''}
            width={width}
            height={height}
            onClick={(e) => onClick?.(identifier ?? '', e)}
            isLoading={isLoading}
            viewType={viewType}
            isCompetition={isCompetition}
            backgroundStyles={backgroundStyles}
          >
            {(isLoading || !backgroundText) ? null : backgroundText}
            {(isLoading || !statusText) ? null : statusText}
            {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'100%'} />}
            {(
              <FlexBox className="meta" width="100%" justify={isRejected ? 'space-between' : isSelectRightNeed ? 'flex-end' : 'flex-start'} onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                <ActionsTools
                  repost={repostButton}
                  bookmark={bookmarkButton}
                  more={more}
                  remove={deleteButton}
                  status={status}
                  select={undefined}
                  actionsVisible={actionsVisible}
                />
              </FlexBox>
            )}
            {!isDesktop && (
              <FlexBox className="meta" direction={isTeam && (team as any)?.showQu ? "row" : "column"} directionMobile={isTeam && (team as any)?.showQu ? "row" : "column"} width="100%">
                {!!rank && <RankBadge rank={rank} />}
                {(!!grants && !isLoading) && (
                  <FlexBox width="100%" className="pcw" height={32} offsetBottom={8} gx={20}>
                    {grants}
                  </FlexBox>
                )}
                {!isMobile && !isLoading && !isSuperPuperMain && <FlexBox width="100%" offsetBottom={4}>
                  {isLoading || is1_9 ? null : title}
                </FlexBox>}
                {!isMobile && subtitle && !(isLoading || is1_9) && <FlexBox offsetBottom={8}>{subtitle}</FlexBox>}
                <FlexBox align="flex-end" width="100%" justify="space-between">
                  <div
                    onClick={(event) => event.stopPropagation()}
                    onMouseEnter={() => toggleTooltip && toggleTooltip(true)}
                    style={{ maxWidth: !isSuperPuperMain ? '70%' : '100%' }}
                  >
                    {!isLoading && isSuperPuperMain && <FlexBox width="100%">
                      {isLoading || is1_9 ? null : title}
                    </FlexBox>}
                    {isSuperPuperMain || isLoading || (isTeam && !isChannel) || is1_9 || isMobile ? null : author}
                  </div>
                  <div> {isLoading ? null : likes}</div>
                </FlexBox>
              </FlexBox>
            )}
            {point}
          </ProjectCardWrapperNew>
        </ProjectCardContainerNew>
      )}

      {location.search.includes('display=doublegrid') && (
        <ProjectCardContainerNew
          onMouseLeave={() => toggleTooltip && toggleTooltip(false)}
          tooltipOpen={tooltipOpen}
          width={width}
          height={height}
          itemType={itemType}
          sizeMult={sizeMult}
          isAgeytomesh={isAgeytomesh}
          isLoading={true}>
          <OverflowContainer>
            <CoverImage isSquare={isSquare} files={titleImages} />
          </OverflowContainer>
          <ProjectCardWrapperDoubleNew
            className={metaAlwaysVisible ? 'meta--visible' : ''}
            onClick={(e) => onClick?.(identifier ?? '', e)}
            isLoading={isLoading}
          >
            {!isDesktop && (
              <FlexBox className="meta" width="100%" justify={isRejected ? 'space-between' : 'flex-start'} onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                <ActionsTools
                  status={status}
                  select={undefined}
                  more={more}
                  bookmark={bookmarkButton}
                  remove={deleteButton}
                  repost={repostButton}
                  actionsVisible={actionsVisible}
                />
              </FlexBox>
            )}
            {!isDesktop && (
              <FlexBox className="meta" direction="column" directionMobile="column" width="100%">
                <FlexBox width="100%" className="pcw" height={32} offsetBottom={8} gx={20}>
                  {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={32} height={32} />}
                  {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={32} height={32} />}
                  {!isLoading ? grants : null}
                </FlexBox>
                {!isCollectionVote && <FlexBox width="100%" offsetBottom={4}>
                  {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={32} width="100%" /> : title}
                </FlexBox>}
                {subtitle && !isLoading && <FlexBox offsetBottom={8}>{subtitle}</FlexBox>}
                <FlexBox align="flex-end" width="100%" justify="space-between">
                  {!isSuperPuperMain && <div
                    onClick={(event) => event.stopPropagation()}
                    onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                    {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={100} /> : author}
                  </div>}
                  <div> {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={25} /> : likes}</div>
                </FlexBox>
              </FlexBox>
            )}
          </ProjectCardWrapperDoubleNew>
        </ProjectCardContainerNew>
      )}

      {(isMobile || is1_9) && (
        <FlexBox directionMobile="column" offsetTop={0} direction="column">
          {isLoading && <>
            <Skeleton offsetTop={8} style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'50%'} height={'23px'} />
            <Skeleton offsetTop={2} style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'14px'} />
          </>}
          {
            <div style={{ position: 'relative' }}>
              {isMobile && (isProgramsTab || isChannel)
                ? (
                  <div style={{ position: 'relative' }}>
                    <TitleMobile isExpanded={isExpanded} onClick={toggle} isClampable={isProgramsTab || isChannel}>
                      <div>{title}</div>
                    </TitleMobile>
                  </div>
                ) : (
                  <TitleMobile>
                    <div>{title}</div>
                  </TitleMobile>
                )}
            </div>
          }
          {subtitle && <FlexBox offsetTop={4}><SubTitleMobile>{subtitle}</SubTitleMobile></FlexBox>}
          {(!isSuperPuperMain && !isTeam) && <AuthorMobile hasSubtitle={Boolean(subtitle)} isDoubleGrid={isDoubleGrid}>{author}</AuthorMobile>}
          {shouldDisplayAnnotation && (
            <TruncateTextOverflow overflowContainerRef={containerRef}>
              <AuthorMobile>{stripHtml(annotation)}</AuthorMobile>
            </TruncateTextOverflow>
          )}
          {!isMedia && !isDoubleGrid && likes}
        </FlexBox>
      )}
    </Link>
  );
};

export default memo(ProjectCardLayoutNew)
import React, { FC, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import { ExternalLink, FlexBox, Skeleton, useBreakpoint, useIsMobile } from '@brg/ui';
import { TFile, Project, Competition, Card } from "@brg/api";
import { COVER_VIEWTYPES_1_9, ProjectCardContainerNew, ProjectCardWrapperDoubleNew, ProjectCardWrapperNew } from './stylesNew';
import { CreateLinkTo } from '../../constants/linkTo';
import './ProjectCardLayoutNew.css';
import { AppTester } from "../../../supporting";
import useIsDoubleGrid from "../../hooks/useIsDoubleGrid";
import ResizedImageEnhanced from "../ResizedImageEnhanced";
import { TeamHolder } from '../TeamCard/TeamHolder';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import { LayoutConst } from 'core/modules/layout.module';
import { ProjectDesignerWrapper } from "../projectDesignerName/styles";
import { SP_VOTE_COLLECTION_SIGN } from "../../pages/VotesAdmin/constants";
import { VoteCollectionCover } from "../../pages/projectEdit/components/VoteCollectionCover/VoteCollectionCover";
import { ModerationType } from "../../pages/teams/const/ModerationTypes";
import { LMSModuleCover } from "../LMSModuleCover/LMSModuleCover";
import { WarholCardCover } from "../WarholCardCover/WarholCardCover";
import { ReactComponent as AgeRestrictedIcon } from '../../assets/ageRestricted.svg';
import { RatingImgGenerate } from 'shared/pages/opencallsPage/GetCompetitionItem/components/RatingImgGenerate';
import { ZharKnigaBadge } from 'shared/pages/opencallsPage/GetCompetitionItem/components/ZharKnigaBadge';
import { useProfile } from '@brg/auth';
import { DesignerCertCardImage } from 'shared/pages/designer/components/tabs/designerDiscountstab';
import { useTranslation } from "react-i18next";
import { ActionsTools } from './components/ActionsTools';
import { OverflowContainer } from './styles';
import { ChannelDefaultEmblem } from 'shared/pages/exploreHseContextsNew/components/ChannelDefaultEmblem';
import { link } from 'fs';
import { BookIconRedMore, BookIconRedMoreDot } from '_deziiign/books/core/components/bookCard/style';
import { useZharKnigaWinner } from 'shared/hooks/useZharKnigaWinner';
import RankBadge from './components/RankBadge';
import mockAnnotations from './assets/mockAnnotanions.json';
import TruncateTextOverflow from '../TruncateTextOverflow/TruncateTextOverflow';
import stripHtml from 'shared/helpers/stripHtml';


const TitleMobile = styled.div<{
  isExpanded?: boolean;
  isClampable?: boolean;
}>`
  margin-top: 16px;

  @media (max-width: ${LayoutConst.screen.md + 'px'}) {
    margin-top: 8px;
  }

  div {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ isExpanded, isClampable }) =>
    isClampable ? (isExpanded ? 'unset' : 3) : 'unset'};
    overflow: ${({ isExpanded, isClampable }) =>
    isClampable ? (isExpanded ? 'visible' : 'hidden') : 'visible'};
    text-overflow: ellipsis;
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 15px;
    line-height: 20px;
    color: ${({ theme }) => theme.projectView.titleColor};
    transition: all 0.3s ease-in-out;
  }
`;


const SubTitleMobile = styled.div`

  div {
    color: ${({ theme }) => theme.projectView.titleColor};
  }
`;

const AuthorMobile = styled.div<{ isDoubleGrid?: boolean, hasSubtitle?: boolean }>`
  margin-top: 8px;
  ${ProjectDesignerWrapper} {
    color: #909090;
  }
  :hover {
    ${ProjectDesignerWrapper} {
      color: ${(p) => p.theme.multipleAuthors.colorHover} !important;
      svg path {
          fill: ${(p) => p.theme.projectView.authorColorHover};
      }
    }
  }

  @media (max-width: ${LayoutConst.screen.md + 'px'}) {
    margin-top: ${({ hasSubtitle }) => hasSubtitle ? 8 : 4}px;
    span {
      color: ${({ theme }) => theme.projectView.madeBy};
    }
  }
  
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: ${({ theme }) => theme.projectView.madeBy};
  
 
`;

export const CoverImage = styled(ResizedImageEnhanced) <{ absolute?: boolean, isSquare?: boolean, isCGRAVE?: boolean, isTeam?: boolean, isLargeProject?: boolean }>`
  position: ${({ absolute }) => absolute ? 'absolute' : 'static'};
  flex-shrink: 0;
  top: 0;
  left: 0;
  height: ${({ isSquare }) => isSquare ? 'initial' : '100%'};
  background: ${({ isCGRAVE }) => isCGRAVE ? '#464645' : ''};
  ${({ isTeam, isLargeProject }) => (isTeam || isLargeProject) && css`
    @media (max-width: ${LayoutConst.screen.s + 'px'}) {
        img{
          object-fit: cover;
        }
      }
  `}
  
`

const MobileMoreWrapper = styled(FlexBox) <{ backgroundStyles?: any }>`
  position: absolute;
  top: 0;
  width: 100%;
  // height: 100%;
  padding-right: 8px;

  ${({ backgroundStyles }) => backgroundStyles && css`
    ${backgroundStyles}
    height: 100%;
  `};
`

export interface ProjectCardLayoutNewProps {
  isLoading?: boolean;
  grants?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  isOneColumn?: boolean;
  coverTitle?: ReactNode;
  author?: ReactNode;
  likes?: ReactNode;
  point?: ReactNode;
  status?: ReactNode;
  backgroundText?: ReactNode;
  img?: string;
  titleImages?: TFile[];
  identifier?: string;
  project?: Project & any;
  team?: ComputedTeam;
  onClick?: (projectId?: string, e?: any) => void;
  isMoreVisible?: boolean;
  isRejected?: boolean;
  more?: ReactNode;
  href?: string;
  width?: string;
  height?: string;
  isModal?: boolean;
  statusText?: ReactNode;
  rank?: number;
  metaAlwaysVisible?: boolean;
  tooltipOpen?: boolean;
  needLmsCover?: boolean;
  toggleTooltip?: Function;
  linkPrefix?: string;
  isSelectRightNeed?: boolean;
  toggleViewPoint?: (flag: boolean) => void;
  sizeMult?: number;
  withoutGoToProject?: boolean;
  isMobileMoreVisible?: boolean;
  isRemovableFromBook?: boolean;
  deleteButton?: ReactNode;
  bookmarkButton?: ReactNode;
  repostButton?: ReactNode;
  bookmarkHovered?: boolean;
  isTeam?: boolean;
  isBookChapter?: boolean;
  isBook?: boolean;
  hasBottomText?: boolean;
  projectType?: string;
  lmsCardType?: string;
  itemType?: string;
  linkTo?: any;
  linkParams?: string;
  isCert?: boolean;
  card?: Card;
  backgroundStyles?: any;
  topBadge?: ReactNode;
  showAnnotaions?: boolean;
}


const AgeRestrictedOverlayRoot = styled(FlexBox)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(222, 222, 222, 0.60) 0%, rgba(92, 92, 92, 0.60) 100%);
  backdrop-filter: blur(10px);
  z-index: 1;
  align-items: center;
  justify-content: center;
`

export const AgeRestrictedOverlay = () => {
  return <AgeRestrictedOverlayRoot>
    <AgeRestrictedIcon />
  </AgeRestrictedOverlayRoot>
}

const Link = styled(ExternalLink) <{ span: number }>`
  display: initial;
  grid-column: span ${({ span }) => span};
  width: 100%;
  max-height: 536px;

  @media (max-width: ${LayoutConst.screen.l + 'px'}) {
    max-height: 410px;
  }
`
const TopBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
`

const ProjectCardLayoutNew: FC<ProjectCardLayoutNewProps> = ({
  isLoading,
  isMoreVisible,
  isRejected,
  isOneColumn,
  grants,
  title,
  subtitle,
  author,
  likes,
  more,
  status,
  width,
  height,
  team,
  statusText,
  metaAlwaysVisible,
  isMobileMoreVisible,
  onClick,
  isBook,
  isModal,
  identifier,
  point,
  rank,
  titleImages,
  isSelectRightNeed,
  sizeMult = 1,
  tooltipOpen = false,
  toggleViewPoint,
  toggleTooltip,
  linkPrefix,
  linkTo,
  withoutGoToProject,
  isRemovableFromBook,
  deleteButton,
  bookmarkButton,
  repostButton,
  isTeam,
  isBookChapter,
  backgroundText,
  backgroundStyles,
  projectType: viewType,
  lmsCardType,
  project,
  itemType,
  linkParams = '',
  coverTitle,
  needLmsCover,
  hasBottomText,
  isCert,
  card,
  topBadge,
  showAnnotaions
}) => {
  const location = useLocation();
  // const isMobile = useIsMobile();
  const isMobile = useBreakpoint(LayoutConst.screen.s - 1)
  const isDesktop = useBreakpoint(LayoutConst.screen.l + 1);
  const isAgeytomesh = AppTester.isAgeytomesh();
  const isDvaDesign = AppTester.isDvaDesign();
  const isSuperPuper = AppTester.isSuperPuper();
  const isCompetition = AppTester.isCompetition();
  const isMedia = AppTester.isMediiia();
  const isSuperPuperMain = useMemo(() => isSuperPuper && location.pathname === '/', [isSuperPuper, location.pathname])
  const isSuperPuperProject = useMemo(() => project?.contexts?.includes('super-puper-collection'), [project])
  const profile = useProfile();
  const isDoubleGrid = useIsDoubleGrid();
  const isSquare = false;
  const is743px = useBreakpoint(743);
  const theme = useTheme();
  const { i18n: { language } } = useTranslation();
  const isHSEDesign = AppTester.isHSEDesign();
  const containerRef = useRef<HTMLAnchorElement>(null);

  const teamWithoutImage = isTeam && team && (!titleImages || titleImages?.length === 0);

  const hasRootTeam = !!project?.rootTeamId;
  const rootTeamRoute = project?.rootTeam?.route;

  const [isExpanded, setIsExpanded] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isProgramsTab = searchParams.get('tab') === 'programs'
  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Добавьте эту строку
    setIsExpanded(!isExpanded);
  };

  const is1_9 = COVER_VIEWTYPES_1_9.includes(String(viewType)) || showAnnotaions;
  const isCollectionVote = useMemo(() => {
    return (!titleImages || !Boolean(titleImages?.length)) && (project?.projectContexts?.includes(SP_VOTE_COLLECTION_SIGN) || project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN))
  }, [project, titleImages])

  const annotation = useMemo(() => {
    if (project) {
      const { annotationText, annotationText_en } = project?.additionalInfo || {};
      if (language === 'en' && annotationText_en) {
        return annotationText_en;
      }
      return annotationText;
    }
  }, [project, language])

  const shouldDisplayAnnotation = useMemo(() => {
    return showAnnotaions && annotation?.length > 0;
  }, [showAnnotaions, annotation])

  const isWrongContext = useMemo(() => {
    return !!project && (
      (isDvaDesign && !(project?.contexts?.includes('dvadesign') || project?.projectContexts?.includes('dvadesign'))));
  }, [project])

  const onLinkClick = useCallback((e: any) => {
    if (hasRootTeam) {
      window.open(rootTeamRoute, '_self');
    } else if (isWrongContext) {
      window.open(process.env.REACT_APP_URL_DEZIIIGN + '/project/' + identifier);
    } else {
      onClick?.(identifier ?? '', e);
    }
  }, [onClick, identifier, isWrongContext])

  const isLMS = useMemo(() => team?.moderationType === ModerationType.LMS, [team?.moderationType])

  const isChannel = useMemo(() => team?.moderationType === ModerationType.Channel, [team?.moderationType])

  // const isUnitCourse = useMemo(() => {
  //   //@ts-ignore
  //   return team?.contexts?.includes(contextName)
  // } , [contextName, team])

  const ratingCompetition = project?.competitions?.find((item: Competition) => item.contexts?.includes("ratings"))
  // @ts-ignore
  const isRatingWinner = ratingCompetition?.nominations?.[0]?.winnerPositions?.some(item => item?.winnerId === project?.projectId) ?? false;

  // Жар-книга competition - проверяем через хук, является ли проект победителем
  const { isWinner: isZharKnigaWinner } = useZharKnigaWinner(project?.projectId);

  const isLMSProject = useMemo(() => project?.contexts?.includes('lmshseproject') || project?.contexts?.includes('lmsproject'), [project])

  const needLMSTeamCover = useMemo(
    () => ((needLmsCover || isLMS) && teamWithoutImage)
    , [isLMS, teamWithoutImage, needLmsCover])

  const LMSDiscipline = useMemo(() => project?.additionalInfo?.lmsmodule_discipline || '', [project])
  const [onhoverPlate, setOnhoverPlate] = useState(true);

  const onContainerMouseLeave = (ev: React.SyntheticEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    toggleTooltip && toggleTooltip(false);
    toggleViewPoint && toggleViewPoint(false);
    setOnhoverPlate(true);
  }

  const onContainerMouseEnter = () => {
    toggleViewPoint && toggleViewPoint(true);
    setOnhoverPlate(false);
  }

  const calcGridColumn = useMemo(
    () => (['t-shape', 'fixed-ratio-small', 'fixed-ratio-large', 'horizontal', 'animated'].includes(String(isTeam ? lmsCardType : viewType)) && !isOneColumn ? 2 : 1), [isOneColumn, lmsCardType, isTeam])

  const isLargeProject = calcGridColumn === 2;


  const isBookMarkHidden = useMemo(() => isSuperPuperProject || isRemovableFromBook, [isSuperPuperProject, isRemovableFromBook])

  const isRepostHidden = useMemo(() => {
    return !profile || isTeam || isLMS || isLMSProject || !!team || !!project?.teams || !!project?.rootTeam
  }, [isTeam, isLMS, isLMSProject, profile, team, project])

  const cardBgColor = useMemo(() => {
    return undefined;
  }, [])

  const actionsVisible = useMemo(() => {
    return {
      loading: isLoading,
      showRemove: !(isLoading || !isRemovableFromBook),
      showMore: !(isLoading || !isMoreVisible || isSelectRightNeed),
      showRepost: !(isLoading || isRepostHidden),
      showBookmark: !(isLoading || isBookMarkHidden),
      showStatus: !(isLoading || !isRejected || !status),
      showStatusText: !(isLoading || !statusText),
      showSelect: false,
      showBannedStatus: project?.status === 3,
    }
  }, [
    isRejected,
    isLoading,
    isMoreVisible,
    statusText,
    isRemovableFromBook,
    isBookMarkHidden,
    isRepostHidden,
    isSelectRightNeed,
    status
  ])

  const onDragStart = (e: React.DragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragOver = (e: React.DragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
  };

  const calcTitle = useMemo(() => {
    if (!!project?.additionalContent) {
      return project?.additionalContent?.title?.text
    }
    return project?.title
  }, [project])

  const linkTitle = useMemo(() => {
    if (backgroundText) {
      return undefined
    }
    return !!calcTitle ? `${language === 'ru' ? 'Ссылка на проект' : 'Link to project'} - ${calcTitle}` : undefined
  }, [project, calcTitle, backgroundText, language])

  const linkHref = useMemo(() => {
    if (isModal || isWrongContext || backgroundText) return undefined
    return !!linkTo ? linkTo : isLoading ? undefined : hasRootTeam ? rootTeamRoute : ((linkPrefix ? linkPrefix : "") + (CreateLinkTo.projectNew(project) + linkParams))
  }, [isModal, withoutGoToProject, backgroundText, isWrongContext, linkTo, isLoading, hasRootTeam, rootTeamRoute, linkPrefix, linkParams])

  return (
    <Link
      {...linkHref ? { to: linkHref } : {}}
      ref={containerRef}
      onClick={onLinkClick}
      span={calcGridColumn}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      title={linkTitle}
    >
      {!location.search.includes('display=doublegrid') && (
        <ProjectCardContainerNew
          onMouseLeave={onContainerMouseLeave}
          onMouseEnter={onContainerMouseEnter}
          {...(isMobile ? {
            onTouchStart: onContainerMouseLeave
          } : {})}
          tooltipOpen={tooltipOpen} // У активной карточки увеличивается z-index и tooltip начинает норм работать 
          width={width}
          height={height}
          isLoading={isLoading}
          isSquare={isSquare}
          noGrid={!isDoubleGrid}
          sizeMult={sizeMult}
          isAgeytomesh={isAgeytomesh}
          cardBgColor={cardBgColor}
          viewType={viewType}
          itemType={itemType}
          hasBottomText={hasBottomText}
        >
          {isBook && !isMobile && <BookIconRedMore><BookIconRedMoreDot isBlue={isHSEDesign} /><BookIconRedMoreDot isBlue={isHSEDesign} /><BookIconRedMoreDot isBlue={isHSEDesign} /></BookIconRedMore>}

          <OverflowContainer isAdaptiveCard={project?.isAdaptiveCard}>
            {project?.isAgeRestricted && <AgeRestrictedOverlay />}
            {/*@ts-ignore*/}
            {!isLoading && (isCert && !!card ? (
              <DesignerCertCardImage card={card} />
            ) : project?.isAdaptiveCard ? (
              <WarholCardCover project={project} />
            ) : isCollectionVote ? (
              <VoteCollectionCover project={project} />
            ) : (teamWithoutImage && !isLMS) ? (
              <ChannelDefaultEmblem team={team} />
            ) : (isLMSProject || needLMSTeamCover) ? (
              <LMSModuleCover project={project} title={coverTitle || title} discipline={LMSDiscipline} />
            ) : (
              <CoverImage
                isSquare={isSquare}
                showEmptyPlaceholder={!isBookChapter}
                showDefaultPlaceholder={isBookChapter}
                files={titleImages}
                isTeam={isTeam}
                isLargeProject={isLargeProject}
                alt={project?.title}
              />
            ))}
            {point}
          </OverflowContainer>
          {/* Шильдики конкурсов */}
          {(isRatingWinner || isZharKnigaWinner) && (
            <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {isRatingWinner && ratingCompetition?.isRating && (
                <RatingImgGenerate competition={ratingCompetition} isGridPlate={true} onhoverPlate={!onhoverPlate} />
              )}
              {isZharKnigaWinner && (
                <ZharKnigaBadge isGridBadge={true} onhoverPlate={!onhoverPlate} />
              )}
            </div>
          )}
          {/* Плашка партнёрского материала */}
          {topBadge && (
            <TopBadge>
              {topBadge}
            </TopBadge>
          )}
          {isMobile && isLoading &&
            <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'100%'} />
          }
          <ProjectCardWrapperNew
            className={metaAlwaysVisible ? 'meta--visible' : ''}
            width={width}
            height={height}
            onClick={(e) => onClick?.(identifier ?? '', e)}
            isLoading={isLoading}
            viewType={viewType}
            isCompetition={isCompetition}
            backgroundStyles={backgroundStyles}
          >
            {(isLoading || !backgroundText) ? null : backgroundText}
            {(isLoading || !statusText) ? null : statusText}
            {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'100%'} />}
            {(
              <FlexBox className="meta" width="100%" justify={isRejected ? 'space-between' : isSelectRightNeed ? 'flex-end' : 'flex-start'} onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                <ActionsTools
                  repost={repostButton}
                  bookmark={bookmarkButton}
                  more={more}
                  remove={deleteButton}
                  status={status}
                  select={undefined}
                  actionsVisible={actionsVisible}
                />
              </FlexBox>
            )}
            {!isDesktop && (
              <FlexBox className="meta" direction={isTeam && (team as any)?.showQu ? "row" : "column"} directionMobile={isTeam && (team as any)?.showQu ? "row" : "column"} width="100%">
                {!!rank && <RankBadge rank={rank} />}
                {(!!grants && !isLoading) && (
                  <FlexBox width="100%" className="pcw" height={32} offsetBottom={8} gx={20}>
                    {grants}
                  </FlexBox>
                )}
                {!isMobile && !isLoading && !isSuperPuperMain && <FlexBox width="100%" offsetBottom={4}>
                  {isLoading || is1_9 ? null : title}
                </FlexBox>}
                {!isMobile && subtitle && !(isLoading || is1_9) && <FlexBox offsetBottom={8}>{subtitle}</FlexBox>}
                <FlexBox align="flex-end" width="100%" justify="space-between">
                  <div
                    onClick={(event) => event.stopPropagation()}
                    onMouseEnter={() => toggleTooltip && toggleTooltip(true)}
                    style={{ maxWidth: !isSuperPuperMain ? '70%' : '100%' }}
                  >
                    {!isLoading && isSuperPuperMain && <FlexBox width="100%">
                      {isLoading || is1_9 ? null : title}
                    </FlexBox>}
                    {isSuperPuperMain || isLoading || (isTeam && !isChannel) || is1_9 || isMobile ? null : author}
                  </div>
                  <div> {isLoading ? null : likes}</div>
                </FlexBox>
              </FlexBox>
            )}
            {point}
          </ProjectCardWrapperNew>
        </ProjectCardContainerNew>
      )}

      {location.search.includes('display=doublegrid') && (
        <ProjectCardContainerNew
          onMouseLeave={() => toggleTooltip && toggleTooltip(false)}
          tooltipOpen={tooltipOpen}
          width={width}
          height={height}
          itemType={itemType}
          sizeMult={sizeMult}
          isAgeytomesh={isAgeytomesh}
          isLoading={true}>
          <OverflowContainer>
            <CoverImage isSquare={isSquare} files={titleImages} />
          </OverflowContainer>
          <ProjectCardWrapperDoubleNew
            className={metaAlwaysVisible ? 'meta--visible' : ''}
            onClick={(e) => onClick?.(identifier ?? '', e)}
            isLoading={isLoading}
          >
            {!isDesktop && (
              <FlexBox className="meta" width="100%" justify={isRejected ? 'space-between' : 'flex-start'} onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                <ActionsTools
                  status={status}
                  select={undefined}
                  more={more}
                  bookmark={bookmarkButton}
                  remove={deleteButton}
                  repost={repostButton}
                  actionsVisible={actionsVisible}
                />
              </FlexBox>
            )}
            {!isDesktop && (
              <FlexBox className="meta" direction="column" directionMobile="column" width="100%">
                <FlexBox width="100%" className="pcw" height={32} offsetBottom={8} gx={20}>
                  {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={32} height={32} />}
                  {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={32} height={32} />}
                  {!isLoading ? grants : null}
                </FlexBox>
                {!isCollectionVote && <FlexBox width="100%" offsetBottom={4}>
                  {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={32} width="100%" /> : title}
                </FlexBox>}
                {subtitle && !isLoading && <FlexBox offsetBottom={8}>{subtitle}</FlexBox>}
                <FlexBox align="flex-end" width="100%" justify="space-between">
                  {!isSuperPuperMain && <div
                    onClick={(event) => event.stopPropagation()}
                    onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                    {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={100} /> : author}
                  </div>}
                  <div> {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={25} /> : likes}</div>
                </FlexBox>
              </FlexBox>
            )}
          </ProjectCardWrapperDoubleNew>
        </ProjectCardContainerNew>
      )}

      {(isMobile || is1_9) && (
        <FlexBox directionMobile="column" offsetTop={0} direction="column">
          {isLoading && <>
            <Skeleton offsetTop={8} style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'50%'} height={'23px'} />
            <Skeleton offsetTop={2} style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'14px'} />
          </>}
          {
            <div style={{ position: 'relative' }}>
              {isMobile && (isProgramsTab || isChannel)
                ? (
                  <div style={{ position: 'relative' }}>
                    <TitleMobile isExpanded={isExpanded} onClick={toggle} isClampable={isProgramsTab || isChannel}>
                      <div>{title}</div>
                    </TitleMobile>
                  </div>
                ) : (
                  <TitleMobile>
                    <div>{title}</div>
                  </TitleMobile>
                )}
            </div>
          }
          {subtitle && <FlexBox offsetTop={4}><SubTitleMobile>{subtitle}</SubTitleMobile></FlexBox>}
          {(!isSuperPuperMain && !isTeam) && <AuthorMobile hasSubtitle={Boolean(subtitle)} isDoubleGrid={isDoubleGrid}>{author}</AuthorMobile>}
          {shouldDisplayAnnotation && (
            <TruncateTextOverflow overflowContainerRef={containerRef}>
              <AuthorMobile>{stripHtml(annotation)}</AuthorMobile>
            </TruncateTextOverflow>
          )}
          {!isMedia && !isDoubleGrid && likes}
        </FlexBox>
      )}
    </Link>
  );
};

export default memo(ProjectCardLayoutNew)
